import { zodResolver } from "@hookform/resolvers/zod";
import { Table, Textarea } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  PredictionNegativeFeedbackFormSchema,
  PredictionNegativeFeedbackFormValues,
} from "#modules/feedback/domain/predictionNegativeFeedbackFormSchema";
import { centsToEuro } from "#shared/helpers/prices";
import { Group } from "#shared/view/components/Group";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { MultiSelectInput } from "#shared/view/designSystem/MultiSelectInput";
import { formatPrice, PredictionFeedbackCauseSchema, PredictionFeedbackCauseType } from "shared";

import { SubmitPredictionFeedbackPayload } from "./useSubmitPredictionFeedback";

const toEuros = (cents?: number | null) =>
  typeof cents === "number" ? centsToEuro(cents) : undefined;

type NegativeFeedbackInitialValues = {
  comment?: string | null;
  causes?: PredictionFeedbackCauseType[];
  actualMaterialPrice?: number | null;
  actualAddedValuePrice?: number | null;
  actualTotalPrice?: number | null;
};

type Props = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (payload: SubmitPredictionFeedbackPayload) => void;
  isSubmitting?: boolean;
  predictedMaterialPrice?: number;
  predictedAddedValuePrice?: number;
  predictedTotalPrice?: number;
  initialValues?: NegativeFeedbackInitialValues;
};

export const PredictionNegativeFeedbackModal = ({
  opened,
  onClose,
  onSubmit,
  isSubmitting = false,
  predictedMaterialPrice,
  predictedAddedValuePrice,
  predictedTotalPrice,
  initialValues,
}: Props) => {
  const { t } = useTranslation();

  const causesData = PredictionFeedbackCauseSchema.options.map((cause) => ({
    value: cause,
    label: t(`feedback.cause.${cause}`),
  }));

  const { control, handleSubmit, reset } = useForm<
    PredictionNegativeFeedbackFormValues,
    unknown,
    SubmitPredictionFeedbackPayload
  >({
    defaultValues: {
      causes: initialValues?.causes ?? [],
      comment: initialValues?.comment ?? "",
      actualMaterialPrice: toEuros(initialValues?.actualMaterialPrice),
      actualAddedValuePrice: toEuros(initialValues?.actualAddedValuePrice),
      actualTotalPrice: toEuros(initialValues?.actualTotalPrice),
    },
    resolver: zodResolver(PredictionNegativeFeedbackFormSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = (payload: SubmitPredictionFeedbackPayload) => {
    onSubmit(payload);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("feedback.negativeModal.title")}
      size="lg"
    >
      <Stack gap="md">
        <Controller
          name="causes"
          control={control}
          render={({ field, fieldState }) => (
            <MultiSelectInput
              required
              label={t("feedback.negativeModal.causesLabel")}
              placeholder={t("feedback.negativeModal.causesPlaceholder")}
              data={causesData}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error && t("feedback.negativeModal.causesRequired")}
              searchable
            />
          )}
        />

        <Controller
          name="comment"
          control={control}
          render={({ field }) => (
            <Textarea
              label={t("feedback.negativeModal.commentLabel")}
              placeholder={t("feedback.negativeModal.commentPlaceholder")}
              value={field.value}
              onChange={field.onChange}
              autosize
              minRows={2}
            />
          )}
        />

        <Stack gap="xs">
          <Text fz="sm" fw={600}>
            {t("feedback.negativeModal.pricesLabel")}
          </Text>

          <Table withRowBorders={false} verticalSpacing={4}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>{t("feedback.negativeModal.predictedColumnLabel")}</Table.Th>
                <Table.Th>{t("feedback.negativeModal.actualColumnLabel")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>{t("feedback.negativeModal.materialPriceRowLabel")}</Table.Td>
                <Table.Td c="dimmed">{formatPrice(predictedMaterialPrice)}</Table.Td>
                <Table.Td>
                  <Controller
                    name="actualMaterialPrice"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        unit="€"
                        small
                      />
                    )}
                  />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>{t("feedback.negativeModal.addedValuePriceRowLabel")}</Table.Td>
                <Table.Td c="dimmed">{formatPrice(predictedAddedValuePrice)}</Table.Td>
                <Table.Td>
                  <Controller
                    name="actualAddedValuePrice"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        unit="€"
                        small
                      />
                    )}
                  />
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>{t("feedback.negativeModal.totalPriceRowLabel")}</Table.Td>
                <Table.Td c="dimmed">{formatPrice(predictedTotalPrice)}</Table.Td>
                <Table.Td>
                  <Controller
                    name="actualTotalPrice"
                    control={control}
                    render={({ field }) => (
                      <NumberInput
                        value={field.value}
                        onChange={field.onChange}
                        min={0}
                        unit="€"
                        small
                      />
                    )}
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Stack>

        <Group justify="end">
          <Button variant="subtle" onClick={handleClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSubmit(submit)} loading={isSubmitting}>
            {t("common.validate")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
